import React from 'react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {ITab, SimpleTabs} from '@techsee/techsee-ui-common/lib/tabs/simple';
import {IWifiAnalyzerScanController} from '../../controller';
import {MyNetworkTab} from './MyNetworkTab';
import {ChannelsTab} from './ChannelsTab';
import {Devices} from './Devices';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import {NetworkTab} from './NetworkTab';
import {IWifiAnalyzerResults} from '../../../../../services/AngularServices/WifiAnalyzerService';
import {ITranslate} from '../../../../../services/LocalizationService';
import {observer} from 'mobx-react';

export enum WifiAnalyzerMode {
    Session = 'session',
    History = 'history'
}

interface ScanResultTabsProps {
    ctrl: IWifiAnalyzerScanController;
    mode: WifiAnalyzerMode;
}

const scanTypeMapping = {
    networkSpeed: 'CONNECTIVITY_GURU.WIFI_SCAN.NETWORKS',
    channelAnalysis: 'CONNECTIVITY_GURU.WIFI_SCAN.CHANNELS',
    networkDiscoveryDevices: 'CONNECTIVITY_GURU.WIFI_SCAN.DEVICES',
    connectivityGuruData: 'CONNECTIVITY_GURU.WIFI_SCAN.MY_NETWORK'
};

@observer
export class ScanResultTabs extends TechseeBaseComponent<ScanResultTabsProps> {
    renderPanel(name: string, createdOn: Date, children: JSX.Element): JSX.Element {
        return <div className={`wifi-analyzer-scan-tabs-container`}>{children}</div>;
    }

    renderInternal(): JSX.Element {
        const {
            ctrl: {
                results,
                isScanning,
                isVisible,
                startScan,
                channelAnalysis,
                currentTab,
                setCurrentTab,
                translate,
                getCurrentColorByPoint,
                getDbmColor
            }
        } = this.props;

        if (!isVisible && !results) {
            return <div />;
        }

        const typedResults = results as IWifiAnalyzerResults;

        const tabs: ITab[] = [];

        if (get(typedResults, 'wifiConnectionInfo')) {
            tabs.push({
                title: translate(scanTypeMapping.connectivityGuruData),
                content: () => <MyNetworkTab getDbmColor={getDbmColor} translate={translate} results={typedResults} />
            });
        }

        if (!isEmpty(get(typedResults, 'channelAnalysis'))) {
            tabs.push({
                title: translate(scanTypeMapping.channelAnalysis),
                content: () => (
                    <ChannelsTab
                        platformType={typedResults?.platformType ?? ''}
                        channelAnalysis={channelAnalysis || []}
                        isScanning={isScanning}
                        onStartScan={startScan}
                        translate={translate}
                        getCurrentColorByPoint={getCurrentColorByPoint}
                    />
                )
            });
        }

        // Connected Devices Tab - show if network discovery devices exist
        if (!isEmpty(typedResults.networkDiscoveryDevices)) {
            tabs.push({
                title: translate(scanTypeMapping.networkDiscoveryDevices),
                content: () => (
                    <Devices
                        networkDiscoveryDevices={typedResults.networkDiscoveryDevices}
                        ssid={typedResults.wifiConnectionInfo?.ssid}
                        translate={translate}
                        platformType={results?.platformType}
                        deviceModel={results?.deviceModel}
                    />
                )
            });
        }
        const networksList = typedResults.surroundingNetworks || typedResults.scanAreaResults || [];

        const otherNetworks = networksList.filter((net) => net?.ssid !== typedResults.wifiConnectionInfo?.ssid);

        // Network Speed Tab - show if wifi connection info exists
        if (!isEmpty(otherNetworks)) {
            tabs.push({
                title: translate(scanTypeMapping.networkSpeed),
                content: () => <NetworkTab translate={translate} results={otherNetworks} getDbmColor={getDbmColor} />
            });
        }

        // If no tabs are available, show a message
        if (tabs.length === 0) {
            return this.renderPanel(
                typedResults.name || 'Scan Results',
                typedResults.createdOn,
                <div className='wifi-analyzer-no-data'>
                    <span>No scan data available</span>
                </div>
            );
        }

        return this.renderPanel(
            typedResults.name || 'Scan Results',
            typedResults.createdOn,
            <div className='wifi-analyzer-scan-simple-tabs'>
                <SimpleTabs
                    uid='wifi-analyzer-scan-tabs'
                    tabsList={tabs}
                    initialTabIndex={currentTab}
                    onChange={setCurrentTab}
                />
            </div>
        );
    }
}
